<template>
  <div>
    <v-stepper-content :step="step" class="py-0 px-10">
      <v-form ref="SearchForm" @submit="submitForm" v-focusNextOnEnter>
        <v-card elevation="4" class="card-search-user-wrapper">
          <p class="description-search-user">
            {{ $t('honda.registration.account.section_1.description') }}
          </p>

          <h4 class="title-search-user">
            {{ $t('honda.registration.account.section_1.title') }}
          </h4>

          <m-email-field
            :disabled="account.withoutEmail"
            :maxlength="100"
            :placeholder="$t('honda.registration.account.section_1.input_1_placeholder')"
            :required="true"
            :rules="emailRules"
            class="my-4"
            data-cy="registrationClientEmail"
            v-model="account.email.value" />

          <v-checkbox
            v-if="!isHondaMapit"
            v-model="account.withoutEmail"
            :label="$t('honda.registration.account.section_1.check_box_text')"></v-checkbox>

          <v-btn
            :disabled="!enabledSearchButton"
            :key="buttonKey"
            :loading="loadingUser"
            @click="checkUserEmail()"
            class="btn-primary d-block mr-0 ml-auto"
            dark>
            {{ $t('honda.registration.account.section_1.button_1_title') }}
          </v-btn>
        </v-card>

        <mapit-dialog :dialog="dialog" @close="closeDialog">
          <template v-slot:body>
            <div class="dialog-subtitle" v-balance-text>
              {{ $t('honda.registration.account.section_1.error_search_email') }}
            </div>

            <div class="dialog-body">
              <div v-if="warningEmail" class="warning-label">
                <v-icon color="warning" class="warning-icon-padding">fa-solid fa-warning</v-icon>

                <span class="text-left" v-balance-text>{{
                  $t('honda.registration.account.section_1.warning-email', {
                    email: account.email.value,
                  })
                }}</span>
              </div>

              <span v-balance-text>{{ $t('honda.registration.account.section_1.success_search_email') }}</span>
            </div>
          </template>

          <template v-slot:footer-center>
            <v-btn :key="buttonKey" class="btn-primary" text @click.once="initNewAccount">
              {{ $t('buttons.next') }}
            </v-btn>
          </template>
        </mapit-dialog>

        <mapit-dialog :dialog="dialogWrongCountry" @close="() => (dialogWrongCountry = false)">
          <template v-slot:body>
            <div class="dialog-subtitle pt-10" v-balance-text>
              {{ $t('honda.registration.account.section_1.error-wrong-country-header') }}
            </div>

            <div class="dialog-body pt-4" v-balance-text>
              {{ $t('honda.registration.account.section_1.error-wrong-country-body') }}
            </div>
          </template>

          <template v-slot:footer-center>
            <v-btn class="btn-primary" @click="() => (dialogWrongCountry = false)">
              {{ $t('buttons.next') }}
            </v-btn>
          </template>
        </mapit-dialog>
      </v-form>
    </v-stepper-content>

    <footer-actions :step="step">
      <template v-slot:left>
        <v-btn class="btn-gray" @click="backStep" dark tabindex="-1" data-cy="search-btn-back">
          {{ $t('buttons.back') }}
        </v-btn>
      </template>
    </footer-actions>
  </div>
</template>

<script lang="ts">
import {Country} from '@mapit/common-utils/dist/types'
import {computed, defineComponent, ref, watch} from '@vue/composition-api'

import coreApi from '@/api/core.api.js'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import {isEmail} from '@/helpers/validations.js'
import {RegistrationTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'
import {accountRegistrationModel, parseRegisteredAccount} from '@/vuex/models/index.js'

import FooterActions from '../FooterActions.vue'

export default defineComponent({
  name: 'Search',
  components: {FooterActions, MapitDialog},
  props: {
    step: Number,
  },
  setup(props, {refs, root, emit}) {
    const {getAccount} = mapGetters(root.$store.getters)

    const account = ref(getAccount.value)
    const buttonKey = ref(1)
    const dialog = ref(false)
    const dialogWrongCountry = ref(false)
    const errorDemoBike = ref(false)
    const loadingUser = ref(false)
    const warningEmail = ref(false)

    const isCreatedFromOffer = computed(() => {
      return root.$store.getters.getForcedType === RegistrationTypes.OFFER_REGISTRATION
    })

    const services = computed(() => {
      return root.$store.getters.getServices
    })

    const isHondaMapit = computed(() => {
      return root.$store.getters.getHasHondaMapitService
    })

    const enabledSearchButton = computed(() => {
      return (
        account.value.withoutEmail ||
        (!account.value.withoutEmail &&
          account.value.email.value &&
          typeof isEmail(account.value.email.value) !== 'string')
      )
    })

    const user = computed(() => {
      return root.$store.getters.getUser
    })

    const country = computed(() => {
      return root.$store.getters.getCountry
    })

    const isTransferVehicleRegistration = computed(() => {
      return (
        root.$route.query.forcedType === RegistrationTypes.HONDA_MAPIT_TRANSFERRED &&
        root.$store.getters.getSelectedTransfer
      )
    })

    const emailRules = computed(() => {
      return account.value.withoutEmail ? [] : [...account.value.email.rules, dealerEmailRule]
    })

    function dealerEmailRule(email: string) {
      return (email && email !== user.value.login) || root.$t('honda.registration.account.section_1.error-demobike')
    }

    watch(
      () => account.value.email.value,
      function () {
        buttonKey.value++
      },
    )

    watch(
      () => account.value.withoutEmail,
      function (val) {
        if (val) {
          const newAccount = accountRegistrationModel()
          newAccount.withoutEmail = val
          root.$store.dispatch('addAccount', newAccount)
        } else {
          root.$store.dispatch('addAccount', account.value)
        }
        errorDemoBike.value = false
        buttonKey.value++
      },
    )

    watch(
      getAccount,
      function (val: any) {
        if (val.withoutEmail !== account.value.withoutEmail || account.value.email.value !== val.email.value) {
          account.value = val
        }
      },
      {deep: true},
    )

    function submitForm(e: any) {
      if (enabledSearchButton.value) {
        checkUserEmail()
      }
      e.preventDefault()
    }

    function initNewAccount() {
      if (!isCreatedFromOffer.value) {
        const newAccount = accountRegistrationModel()
        newAccount.email = account.value.email
        newAccount.withoutEmail = account.value.withoutEmail
        goToSecondPart(newAccount)
      } else {
        goToSecondPart()
      }
    }

    function goToSecondPart(act = account.value) {
      if (act.email.value === user.value.login) {
        errorDemoBike.value = true
      } else {
        errorDemoBike.value = false
        root.$store.dispatch('addAccount', act).then(() => {
          emit('nextStep')
          closeDialog()
          buttonKey.value++
        })
      }
    }

    function backStep() {
      account.value.withoutEmail = false
      errorDemoBike.value = false

      if (!isTransferVehicleRegistration.value) {
        emit('backStep')
      } else {
        root.$router.push({name: 'TransferredVehicles'})
      }
    }

    function checkUserEmail() {
      // @ts-ignore
      if (account.value.withoutEmail || (refs.SearchForm.validate() && enabledSearchButton.value)) {
        handleUserEmail()
      }
    }

    async function handleUserEmail() {
      if (account.value.withoutEmail) {
        account.value.email.value = ''
        return goToSecondPart()
      }

      if (account.value.email.value === user.value.login && !services.value['honda-mapit'].selected) {
        errorDemoBike.value = true
        buttonKey.value++
        return
      } else {
        errorDemoBike.value = false
        warningEmail.value = false
        loadingUser.value = true

        if (process.env.VUE_APP_CORE_EMAIL_VALIDATION === 'true') {
          try {
            const {data: emailValidation} = await coreApi.validateEmail({
              email: account.value.email.value,
            })

            if (!emailValidation.valid && !emailValidation.error) {
              warningEmail.value = true
            }
          } catch (e) {
            console.error('An unexpected error occurred while validating email.', e)
          }
        }

        return coreApi
          .getAccountByEmail(account.value.email.value)
          .then(({data}: any) => {
            const account = data[0]
            if (account) {
              const isFromDifferentCountry =
                account.address && (account.address.country || Country.Spain) !== country.value
              const hasActiveVehicles = account.ownedVehicles && account.ownedVehicles.length > 0

              if (isFromDifferentCountry && hasActiveVehicles) {
                dialogWrongCountry.value = true
              } else if (account.id) {
                const registeredAccount = parseRegisteredAccount(account)
                goToSecondPart(registeredAccount)
              }
            } else showDialog()
          })
          .catch((e: any) => {
            console.log('Error signing docs: ', e)
            showDialog()
          })
          .finally(() => {
            loadingUser.value = false
            buttonKey.value++
          })
      }
    }

    function closeDialog() {
      dialog.value = false
    }

    function showDialog() {
      dialog.value = true
    }

    return {
      account,
      backStep,
      buttonKey,
      checkUserEmail,
      closeDialog,
      dialog,
      dialogWrongCountry,
      emailRules,
      enabledSearchButton,
      errorDemoBike,
      initNewAccount,
      isCreatedFromOffer,
      isHondaMapit,
      isTransferVehicleRegistration,
      loadingUser,
      services,
      submitForm,
      user,
      warningEmail,
    }
  },
})
</script>

<style lang="scss">
.warning-icon-padding {
  padding: 0 14px;
}

.card-search-user-wrapper {
  margin: 30px auto;
  width: 50%;
  border: 2px solid var(--v-gray-lighten2);
  padding: 40px;
}

.title-search-user {
  font: normal normal 600 24px/30px 'ProximaNovaExCnSbold';
  letter-spacing: 0;
  color: var(--v-primary-base);
  text-transform: uppercase;
  opacity: 1;
  font-size: 24px;
}

.description-search-user {
  font-family: 'ProximaNovaRegular', sans-serif;
  font-size: 14px;
  letter-spacing: 0;
}

.email-text-field-search-user {
  margin: 18px 0;

  label {
    margin: -5px 0;
  }
}

.dialog-subtitle {
  color: black;
  font-size: 24px !important;
  letter-spacing: 0.48px;
  line-height: 30px;
  text-transform: uppercase;
  font-family: 'ProximaNovaExCnSbold', sans-serif;
  text-align: center;
}

.dialog-body {
  margin-top: 16px;
  font-size: 15px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.warning-label {
  font-family: 'ProximaNovaRegular', sans-serif;
  font-weight: bold;
  padding: 15px;
  width: 95%;
  display: flex;
  justify-content: center;
  color: #fca400;
  background-color: rgba(243, 201, 125, 0.3);
  margin-bottom: 8px;
  border-radius: 6px;

  i {
    margin-right: 10px;
  }
}
</style>
